import { FC, memo, useCallback, useState } from 'react'
import { CrewScrollView } from 'components/devextreme/crewScrollView'
import { useTranslation } from '@crew/modules/i18n'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewErrorBoundary } from 'components/functions/crewErrorBoundary'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useChatStartGroupTabPanel } from './useChatStartGroupTabPanel'
import { ChatStartGroupTabToolbar } from './components/chatStartGroupTabToolbar/chatStartGroupTabToolbar'
import { ChatStartGroupTabUserList } from './components/chatStartGroupTabUserList/chatStartGroupTabUserList'
import { EntityType } from '@crew/enums/domain'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { getDefaultTabValue } from '@crew/utils/enum'
import { GroupChatTabs } from 'enums/app'

type ChatStartGroupTabPanelProps = {
  onClose: () => void
}

// グループタブ
export const ChatStartGroupTabPanel: FC<ChatStartGroupTabPanelProps> = memo(
  (props) => {
    const {
      insertGroupChat,
      isInsertingGroupChat,
      insertFavorite,
      isInsertingFavorite,
      // insertInstantChannel,
      // insertFavorite,
    } = useChatStartGroupTabPanel()

    const { t } = useTranslation()
    const { navigate } = useCrewNavigate()
    const [showApiErrors] = useShowApiErrors()

    // 選択されたユーザIDリスト
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])

    // ユーザ選択状態変更時
    const handleUserSelectionChange = useCallback(
      (userId: string, checked: boolean) => {
        setSelectedUserIds((prev) => {
          if (checked) {
            // チェックされた場合、配列に追加
            return [...prev, userId]
          } else {
            // チェック解除された場合、配列から削除
            return prev.filter((id) => id !== userId)
          }
        })
      },
      []
    )

    // 開始ボタンクリック時
    const handleStartButtonClick = useCallback(async () => {
      try {
        // グループチャットを作成
        const result = await insertGroupChat(selectedUserIds)
        if (!result?.groupChat?.id) return

        // お気に入りとして登録
        await insertFavorite(EntityType.Project, result.groupChat.id)

        // 登録したグループチャット画面へ遷移
        navigate(
          `/group-chats/${result.groupChat.id}/${getDefaultTabValue(
            GroupChatTabs
          )}`
        )
      } catch (err) {
        showApiErrors(err)
        return
      }

      // ダイアログを閉じる
      props.onClose()
    }, [
      insertFavorite,
      insertGroupChat,
      navigate,
      props,
      selectedUserIds,
      showApiErrors,
    ])

    return (
      <div className="flex flex-col h-full gap-y-2">
        {/* ツールバー */}
        <CrewErrorBoundary>
          <ChatStartGroupTabToolbar />
        </CrewErrorBoundary>

        {/* ユーザー一覧 */}
        <CrewScrollView className="flex-1 min-h-0">
          <div className="flex flex-col gap-y-5">
            {/* モーダルの最小幅を制限し、ユーザ一覧が正しく表示されるようにする */}
            <div className="overflow-x-auto">
              <div className="min-w-[400px] flex flex-col gap-y-2.5">
                <ChatStartGroupTabUserList
                  onUserSelected={handleUserSelectionChange}
                  isLoading={isInsertingGroupChat || isInsertingFavorite}
                  selectedUserIds={selectedUserIds}
                />
              </div>
            </div>
          </div>
        </CrewScrollView>

        <div className="flex justify-end gap-x-2">
          {/* 開始ボタン */}
          <CrewButton
            text={t('action.start')}
            type="primary"
            stylingMode="contained"
            onClick={handleStartButtonClick}
            disabled={
              // 登録処理中はボタンを無効化
              isInsertingGroupChat || isInsertingFavorite
              // 選択ユーザーがいない場合は自分だけのチャンネルを作成するため、ボタンは有効のままで良い
            }
          />
          {/* 閉じるボタン */}
          <CrewButton
            text={t('action.close')}
            type="normal"
            stylingMode="outlined"
            onClick={props.onClose}
          />
        </div>
      </div>
    )
  }
)
